/**
 * OCC workbench hooks (S4.3) — read-only by design.
 *
 * Every hook reads a PURE PROJECTION endpoint (S4.2 read models); the
 * workbench performs ZERO server writes — acknowledge/snooze are client-side
 * coordination marks in `occStore`, and all write-back happens in the owning
 * module reached via 鑽取來源 drill-through.
 *
 * Cache contract (house rules): static `/api/`-prefixed cacheKeys, tracked
 * through `cacheKeyRegistry` so the SO / PO / shipping mutation hooks'
 * `clearCacheWhere(key.startsWith('/api/occ'))` invalidations reach them.
 * Lens filtering is client-side over the mock-scale projection, keeping the
 * keys static (one cache entry per lens, not per filter combination).
 */

import { useRequest, clearCache } from 'ahooks';
import { useTranslation } from 'react-i18next';
import { purchaseOrderApi, shippingApi } from '@/services/api';
import { occLensApi } from '@/services/api/occ.api';
import { trackCacheKey } from '@/utils/cacheKeyRegistry';
import type { FulfillmentNodeCode, FulfillmentNodeConfig } from '@/types/api/occ';
import type { ShipmentBatch } from '@/types/api/shipping';
import type { Allocation } from '@/types/api/allocation';
import type { OccInvoiceStatusRow, OccReceiptStatusRow } from '@/lib/occ/selectors';

// ---------------------------------------------------------------------------
// Cache keys (static, /api/-prefixed, tracked)
// ---------------------------------------------------------------------------

const OCC_BOARD_CACHE_KEY = '/api/occ/board?view=workbench';
const OCC_LINES_CACHE_KEY = '/api/occ/lines?view=workbench';
const OCC_EXCEPTIONS_CACHE_KEY = '/api/occ/exceptions?view=workbench';
const OCC_NODES_CACHE_KEY = '/api/occ/fulfillment-nodes';
const OCC_BATCH_LENS_CACHE_KEY = '/api/occ/batch-lens?view=workbench';
const OCC_INVOICE_STATUS_CACHE_KEY = '/api/occ/invoice-status?view=workbench';
const OCC_RECEIPT_STATUS_CACHE_KEY = '/api/occ/receipt-status?view=workbench';
const OCC_ALLOCATIONS_CACHE_KEY = '/api/allocations?view=occ-workbench';
const OCC_BATCHES_CACHE_KEY = '/api/shipment-batches?view=occ-workbench';

/** Clear every workbench cache (page-level retry / manual refresh). */
export function invalidateOccWorkbenchCaches(): void {
  clearCache([
    OCC_BOARD_CACHE_KEY,
    OCC_LINES_CACHE_KEY,
    OCC_EXCEPTIONS_CACHE_KEY,
    OCC_BATCH_LENS_CACHE_KEY,
    OCC_INVOICE_STATUS_CACHE_KEY,
    OCC_RECEIPT_STATUS_CACHE_KEY,
    OCC_ALLOCATIONS_CACHE_KEY,
    OCC_BATCHES_CACHE_KEY,
  ]);
}

// ---------------------------------------------------------------------------
// Board roll-up — stat cards + 訂單進度總覽 rows (+ legacy allocation spine)
// ---------------------------------------------------------------------------

export function useOccBoard() {
  const { data, error, loading, run } = useRequest(() => occLensApi.board(), {
    cacheKey: trackCacheKey(OCC_BOARD_CACHE_KEY),
  });

  return {
    // Absent stats stay absent (tiles render '-'), NEVER zero-filled (F2.3).
    stats: data?.payload?.stats,
    orders: data?.payload?.orders ?? [],
    /** Legacy spine rows — the S3.2 checkpoint seam the 供應商確認 lens projects. */
    legacyLines: data?.payload?.data ?? [],
    syncedAt: data?.payload?.syncedAt,
    /** true ONLY when the board route is missing on this seam (F2.3). */
    unavailable: data?.unavailable === true,
    error,
    isLoading: loading,
    refresh: () => {
      clearCache([OCC_BOARD_CACHE_KEY]);
      return run();
    },
  };
}

// ---------------------------------------------------------------------------
// Owned lens rows
// ---------------------------------------------------------------------------

export function useOccLines() {
  const { data, error, loading, run } = useRequest(() => occLensApi.lensLines(), {
    cacheKey: trackCacheKey(OCC_LINES_CACHE_KEY),
  });

  return {
    lines: data?.payload?.data ?? [],
    syncedAt: data?.payload?.syncedAt,
    unavailable: data?.unavailable === true,
    error,
    isLoading: loading,
    refresh: () => {
      clearCache([OCC_LINES_CACHE_KEY]);
      return run();
    },
  };
}

export function useOccExceptions() {
  const { data, error, loading, run } = useRequest(() => occLensApi.exceptions(), {
    cacheKey: trackCacheKey(OCC_EXCEPTIONS_CACHE_KEY),
  });

  return {
    rows: data?.payload?.data ?? [],
    // Absent summary stays absent — chip counts hide instead of claiming 0.
    summary: data?.payload?.summary,
    syncedAt: data?.payload?.syncedAt,
    unavailable: data?.unavailable === true,
    error,
    isLoading: loading,
    refresh: () => {
      clearCache([OCC_EXCEPTIONS_CACHE_KEY]);
      return run();
    },
  };
}

export function useOccBatchLens() {
  const { data, error, loading, run } = useRequest(() => occLensApi.batchLens(), {
    cacheKey: trackCacheKey(OCC_BATCH_LENS_CACHE_KEY),
  });

  return {
    rows: data?.payload?.data ?? [],
    syncedAt: data?.payload?.syncedAt,
    unavailable: data?.unavailable === true,
    error,
    isLoading: loading,
    refresh: () => {
      clearCache([OCC_BATCH_LENS_CACHE_KEY]);
      return run();
    },
  };
}

// ---------------------------------------------------------------------------
// Referenced lenses (read-only, SourceStampTag on every row)
// ---------------------------------------------------------------------------

export function useOccInvoiceStatus() {
  const { data, error, loading, run } = useRequest(() => occLensApi.invoiceStatus(), {
    cacheKey: trackCacheKey(OCC_INVOICE_STATUS_CACHE_KEY),
  });

  return {
    rows: data?.payload?.data ?? [],
    syncedAt: data?.payload?.syncedAt,
    unavailable: data?.unavailable === true,
    error,
    isLoading: loading,
    refresh: () => {
      clearCache([OCC_INVOICE_STATUS_CACHE_KEY]);
      return run();
    },
  };
}

export function useOccReceiptStatus() {
  const { data, error, loading, run } = useRequest(() => occLensApi.receiptStatus(), {
    cacheKey: trackCacheKey(OCC_RECEIPT_STATUS_CACHE_KEY),
  });

  return {
    rows: data?.payload?.data ?? [],
    syncedAt: data?.payload?.syncedAt,
    unavailable: data?.unavailable === true,
    error,
    isLoading: loading,
    refresh: () => {
      clearCache([OCC_RECEIPT_STATUS_CACHE_KEY]);
      return run();
    },
  };
}

// ---------------------------------------------------------------------------
// 進度點 config — late-bound labels (chips NEVER render raw codes)
// ---------------------------------------------------------------------------

export function useOccNodeConfigs() {
  const { i18n } = useTranslation();
  const { data, error, loading } = useRequest(() => occLensApi.fulfillmentNodes(), {
    cacheKey: trackCacheKey(OCC_NODES_CACHE_KEY),
  });

  const configs: FulfillmentNodeConfig[] = data?.payload?.data ?? [];
  const useZh = i18n.language.startsWith('zh');

  /** Localized config label for a code; empty string while configs load. */
  const nodeLabel = (code: FulfillmentNodeCode): string => {
    const config = configs.find((row) => row.code === code);
    if (!config) return '';
    return useZh ? config.nameZh : config.nameEn;
  };

  return { configs, nodeLabel, error, isLoading: loading };
}

// ---------------------------------------------------------------------------
// Document index — the LinkedDocumentRail / drill-route joins
// ---------------------------------------------------------------------------

export interface OccDocIndex {
  /** PO ref for a PO line id (from the first-class Allocation join). */
  poForPoLine: (poLineId: string) => { poId: string; poNumber: string } | undefined;
  /** 裝櫃批次 rows containing a fulfillment line. */
  batchesForLine: (occLineId: string) => ShipmentBatch[];
  /** Earliest ETA across the batches carrying a line, if any. */
  etaForLine: (occLineId: string) => string | undefined;
  invoicesForSo: (soId: string) => OccInvoiceStatusRow[];
  receiptsForPo: (poId: string) => OccReceiptStatusRow[];
}

export function useOccDocIndex(): OccDocIndex & { isLoading: boolean } {
  const allocs = useRequest(() => purchaseOrderApi.listAllocations(), {
    cacheKey: trackCacheKey(OCC_ALLOCATIONS_CACHE_KEY),
  });
  const batches = useRequest(() => shippingApi.listBatches(), {
    cacheKey: trackCacheKey(OCC_BATCHES_CACHE_KEY),
  });
  const invoices = useRequest(() => occLensApi.invoiceStatus(), {
    cacheKey: trackCacheKey(OCC_INVOICE_STATUS_CACHE_KEY),
  });
  const receipts = useRequest(() => occLensApi.receiptStatus(), {
    cacheKey: trackCacheKey(OCC_RECEIPT_STATUS_CACHE_KEY),
  });

  const allocationRows: Allocation[] = allocs.data?.data ?? [];
  const batchRows: ShipmentBatch[] = batches.data?.data ?? [];
  const invoiceRows: OccInvoiceStatusRow[] = invoices.data?.payload?.data ?? [];
  const receiptRows: OccReceiptStatusRow[] = receipts.data?.payload?.data ?? [];

  const poForPoLine = (poLineId: string) => {
    const row = allocationRows.find((alloc) => alloc.poLineId === poLineId);
    return row ? { poId: row.poId, poNumber: row.poNumber } : undefined;
  };

  const batchesForLine = (occLineId: string) =>
    batchRows.filter((batch) => batch.lines.some((line) => line.occLineId === occLineId));

  const etaForLine = (occLineId: string) => {
    const etas = batchesForLine(occLineId)
      .map((batch) => batch.eta)
      .filter(Boolean)
      .sort();
    return etas[0];
  };

  const invoicesForSo = (soId: string) => invoiceRows.filter((row) => row.soId === soId);

  const receiptsForPo = (poId: string) => receiptRows.filter((row) => row.poId === poId);

  return {
    poForPoLine,
    batchesForLine,
    etaForLine,
    invoicesForSo,
    receiptsForPo,
    isLoading: allocs.loading || batches.loading || invoices.loading || receipts.loading,
  };
}
